// import { getAccessTokenAsync } from './auth/auth';

import { useMsal } from '@azure/msal-react';
import { HubConnectionBuilder } from '@microsoft/signalr';
import { getAccessTokenAsync } from './auth';

const apiBase: string = process.env.REACT_APP_API_URL ?? '';
const webhookBase: string = process.env.REACT_APP_WEBHOOK_URL ?? '';

export const useApi = () => {

    const { instance } = useMsal();

    return {
        async delete<T>(path: string, data: object): Promise<T> {
            const res = await fetch(pathJoin(apiBase, path), {
                method: 'DELETE',
                headers: {
                    Authorization: 'Bearer ' + (await getAccessTokenAsync(instance)),
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(data),
            });

            if (res.status === 200) return res.json() as T;
            else return {} as T;
        },

        async patch<T>(path: string, data: object): Promise<T> {
            const res = await fetch(pathJoin(apiBase, path), {
                method: 'PATCH',
                headers: {
                    Authorization: 'Bearer ' + (await getAccessTokenAsync(instance)),
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(data),
            });

            return res.json() as T;
        },

        async put<T>(path: string, data: object): Promise<T> {
            const res = await fetch(pathJoin(apiBase, path), {
                method: 'PUT',
                headers: {
                    Authorization: 'Bearer ' + (await getAccessTokenAsync(instance)),
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(data),
            });

            if (res.status === 200) return res.json() as T;
            else return {} as T;
        },

        async post<T>(path: string, data: object): Promise<T> {
            const res = await fetch(pathJoin(apiBase, path), {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + (await getAccessTokenAsync(instance)),
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(data),
            });

            return res.json() as T;
        },

        async get<T>(
            path: string,
            data: { [key: string]: string } = {}
        ): Promise<T> {
            const res = await fetch(getUrl(path, data), {
                method: 'GET',
                headers: {
                    Authorization: 'Bearer ' + (await getAccessTokenAsync(instance)),
                    Accept: 'application/json',
                },
            });

            return res.json() as T;
        },

        async connect() {
            console.log('Creating connection');
            const connection = new HubConnectionBuilder()
                .withUrl(pathJoin(webhookBase, 'app'), {
                    accessTokenFactory: async () => getAccessTokenAsync(instance),
                })
                .withAutomaticReconnect({
                    // Never give up. The default policy stops after ~30 seconds.
                    nextRetryDelayInMilliseconds: (ctx) => {
                        if (ctx.elapsedMilliseconds < 60_000) return 2000;
                        if (ctx.elapsedMilliseconds < 300_000) return 5000;
                        return 15_000;
                    },
                })
                .build();

            // Catch dead connections faster.
            // Note: serverTimeout must be >= 2x server's KeepAliveInterval (server default = 15s).
            connection.serverTimeoutInMilliseconds = 60_000;
            connection.keepAliveIntervalInMilliseconds = 15_000;

            return connection;
        },
    };
};

const getUrl = (path: string, data: { [key: string]: string }) => {
    let url = new URL(pathJoin(apiBase, path));

    for (let param in data) {
        url.searchParams.append(param, data[param]);
    }

    return url.href;
};

export const pathJoin: (...args: string[]) => string = (...args) =>
    args.length === 1
        ? args?.[0]
        : pathJoin(
              ...[
                  (args?.[0]?.toString().endsWith('/')
                      ? args[0].slice(0, -1)
                      : args[0]) +
                      '/' +
                      (args?.[1]?.toString().startsWith('/')
                          ? args[1].slice(1)
                          : args[1]),
                  ...args.splice(2),
              ]
          );
